import React, { useCallback, useEffect, useRef } from 'react';
import { useHistory, useTranslation } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { WeddingCodeBanner } from './WeddingCodeBanner';
import { WeddingCodeBannerSlot } from './WeddingCodeBannerSlot';
import { useWeddingCodeBannerEligibility } from './useWeddingCodeBannerEligibility';

export type WeddingCodeBannerContainerProps = Readonly<{
  eventHandle: string;
  event: GuestSiteEvent;
}>;

/**
 * Connects the presentational {@link WeddingCodeBanner} to guest-site data: it resolves per-guest
 * eligibility, builds the localized copy with the code interpolated, fires impression/click
 * telemetry, and navigates to accommodations.
 *
 * Renders nothing until eligibility is confirmed, then mounts {@link WeddingCodeBannerSlot} and the
 * banner together: the slot takes the banner's height in flow and the `position: fixed` banner lands
 * on it, so the banner never covers content. Reserving the space speculatively (before the
 * eligibility reads land) is deliberately avoided — on ineligible events it painted a visible strip
 * that then collapsed (PRNT-4836 follow-up).
 */
export const WeddingCodeBannerContainer: React.FC<WeddingCodeBannerContainerProps> = ({ eventHandle, event }) => {
  const { tq } = useTranslation('guestSiteTravel');
  const telemetry = useGuestSiteTelemetry();
  const history = useHistory();

  const weddingCode = event.info.weddingCode;
  const { isEligible, accommodationsPath, bannerUtm } = useWeddingCodeBannerEligibility({
    eventId: event.id,
    eventHandle,
    weddingCode,
  });

  // event_service TRAV-4400: bucketed by JoyEvent (this event's firebaseId), matching
  // event_service's own evaluation of the same flag, so this banner and the backend-authored
  // accommodations note never disagree about which variant this event is in.
  // Skipped until the banner is actually eligible to render — otherwise every guest-site mount
  // would register an Amplitude exposure, including events with no code/hotel/RSVP that never see
  // either copy variant, corrupting the experiment (joy-crit).
  const [isNoteGuestTrackingTreatment, isLoadingNoteGuestTrackingFlag] = useIsFeatureEnabled('accommodationsNoteCopies', {
    skip: !isEligible || !weddingCode,
  });

  // Not ready until the flag has resolved — otherwise the banner briefly renders the control copy
  // and logs a view for it before flipping to the correct variant a moment later (joy-crit).
  const isReady = isEligible && !!weddingCode && !isLoadingNoteGuestTrackingFlag;

  // Fire the impression once, the first time the banner becomes ready to render.
  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (isReady && !hasTrackedView.current) {
      hasTrackedView.current = true;
      telemetry.weddingCodeBannerViewed(weddingCode);
    }
  }, [isReady, weddingCode, telemetry]);

  const handleNavigate = useCallback(() => {
    if (weddingCode) {
      // Same UTM as the path below, so the click event and the resulting page-view agree.
      telemetry.weddingCodeBannerClicked(weddingCode, bannerUtm);
    }
    // In-SPA navigation (accommodations is a guest-site route): keeps the SPA intact and lets the
    // click telemetry above flush, instead of being dropped by a full-page unload.
    history.push(accommodationsPath);
  }, [weddingCode, telemetry, bannerUtm, accommodationsPath, history]);

  if (!isReady) {
    return null;
  }

  // Banner copy from the Figma design (node 54145-35770). The code renders in the canonical
  // `-AND-` format (e.g. EMMY-AND-JOHN) — the Figma's `EMMA-RYAN` is a placeholder.
  // Two full sentences (not runtime concatenation) mirror event_service's own noteTreatment /
  // noteTreatmentGuestTracking split — the translation pipeline wants whole sentences, not a
  // fragment spliced into an existing one.
  const message = isNoteGuestTrackingTreatment
    ? tq`🏨 The couple has arranged guest lodging through Joy. Please book your room using group code ${weddingCode}. Booking through our site allows us to know where everyone is staying to help with our planning.`
    : tq`🏨 The couple has arranged guest lodging through Joy. Please book your room using group code ${weddingCode}.`;
  // Concise accessible name for the interactive banner — screen readers announce this instead of the
  // full marketing sentence.
  const ariaLabel = tq`Book your room with group code ${weddingCode}`;

  return (
    <>
      <WeddingCodeBannerSlot message={message} />
      <WeddingCodeBanner
        message={message}
        ariaLabel={ariaLabel}
        onNavigate={handleNavigate}
      />
    </>
  );
};

WeddingCodeBannerContainer.displayName = 'WeddingCodeBannerContainer';
